import { nanoid } from 'nanoid';

// One identity per browsing context (tab), stable across reloads via sessionStorage. The escape-hatch
// registry is keyed by this rather than the `/viewer/{id}` slot, which buys three properties at once:
// two tabs on the same slot count as two distinct observers (quorum is honest); multiple hook
// instances in one tab collapse to a single observer (no double-counting); and a reload reuses the
// SAME id, so the tab overwrites its own pre-reload entry instead of stranding a phantom exhausted
// vote that a peer would keep counting for the full TTL. sessionStorage is per-tab and survives
// reload but not tab close — exactly this lifetime.
//
// Lives in its own module rather than in coordination.ts because state.ts's persistence effect now
// needs it too, and coordination.ts already imports from state.ts — sharing it through either of
// those would be a cycle.
const TAB_ID_STORAGE_KEY = 'viewer.escapeHatch.tabId';

const resolveTabId = (): string => {
  try {
    const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existing != null && existing !== '') return existing;
    const fresh = nanoid();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage can throw (privacy mode / disabled storage) — fall back to a per-context id.
    return nanoid();
  }
};

export const TAB_ID = resolveTabId();
